import type { RuleCatalogueEntry } from '@/types/api'

/**
 * Reference catalogue helpers (`GET /rules/catalogue`): picker groups, labels and declared
 * types. Engine refs are always available, even before the catalogue loads.
 */

export type RefGroup = 'variables' | 'location' | 'data' | 'time' | 'player' | 'event'

export const REF_GROUP_LABELS: Readonly<Record<RefGroup, string>> = {
  variables: 'Variables',
  location: 'Location & display',
  data: 'Data',
  time: 'Time & date',
  // Cloud-only refs (Signetix Flow, SIGNETIX_FLOW.md §4): never offered by the rule catalogue.
  player: 'Player',
  event: 'Trigger event',
}

export const ENGINE_CATALOGUE: readonly RuleCatalogueEntry[] = [
  { ref: 'time.local', label: 'Time of day', type: 'time', group: 'time', namespace: 'time' },
  { ref: 'date.local', label: 'Date', type: 'date', group: 'time', namespace: 'date' },
  { ref: 'date.weekday', label: 'Day of the week (1 = Monday)', type: 'number', group: 'time', namespace: 'date' },
  { ref: 'date.day', label: 'Day of the month', type: 'number', group: 'time', namespace: 'date' },
  { ref: 'date.month', label: 'Month (1–12)', type: 'number', group: 'time', namespace: 'date' },
  { ref: 'date.year', label: 'Year', type: 'number', group: 'time', namespace: 'date' },
]

export function namespaceOf(ref: string): string {
  return /^[a-z]+/.exec(ref)?.[0] ?? ''
}

export function refGroup(entry: Pick<RuleCatalogueEntry, 'ref' | 'namespace'>): RefGroup {
  const ns = entry.namespace || namespaceOf(entry.ref)
  if (ns === 'var') return 'variables'
  if (ns === 'data') return 'data'
  if (ns === 'time' || ns === 'date') return 'time'
  if (ns === 'player') return 'player'
  if (ns === 'event') return 'event'
  return 'location'
}

/** Server entries plus missing engine refs, de-duplicated by ref. */
export function withEngineRefs(entries: readonly RuleCatalogueEntry[] | null | undefined): RuleCatalogueEntry[] {
  const out = [...(entries ?? [])]
  for (const e of ENGINE_CATALOGUE) if (!out.some((x) => x.ref === e.ref)) out.push(e)
  return out
}

export function groupCatalogue(entries: readonly RuleCatalogueEntry[]): { group: RefGroup; label: string; entries: RuleCatalogueEntry[] }[] {
  return (Object.keys(REF_GROUP_LABELS) as RefGroup[])
    .map((group) => ({ group, label: REF_GROUP_LABELS[group], entries: entries.filter((e) => refGroup(e) === group) }))
    .filter((g) => g.entries.length > 0)
}

/** `data.<key>` source entry a data ref belongs to. */
export function dataSourceEntry(entries: readonly RuleCatalogueEntry[], ref: string): RuleCatalogueEntry | undefined {
  const key = /^data\.([a-z][a-z0-9_]*)/.exec(ref)?.[1]
  return key ? entries.find((e) => e.ref === `data.${key}`) : undefined
}

/** Human label of a ref: catalogue label, or `<source> › path` for data paths, or the ref itself. */
export function refLabel(entries: readonly RuleCatalogueEntry[], ref: string): string {
  const hit = entries.find((e) => e.ref === ref)
  if (hit) return hit.label
  const engine = ENGINE_CATALOGUE.find((e) => e.ref === ref)
  if (engine) return engine.label
  if (ref.startsWith('data.')) {
    const src = dataSourceEntry(entries, ref)
    const key = /^data\.([a-z][a-z0-9_]*)/.exec(ref)?.[1] ?? ''
    const rest = ref.slice(`data.${key}`.length).replace(/^\./, '')
    const path = rest.replace(/\[\?([^=\]]+)=([^\]]+)\]/g, ' ($1 $2)').replace(/\[(\d+)\]/g, ' #$1').split('.').filter(Boolean).join(' › ')
    return path ? `${src?.label ?? key} › ${path}` : (src?.label ?? key)
  }
  if (ref.startsWith('event.') || ref.startsWith('event[')) {
    const path = ref.slice('event'.length).replace(/^\./, '').replace(/\[(\d+)\]/g, ' #$1').split('.').filter(Boolean).join(' › ')
    return `Event › ${path}`
  }
  return ref
}

/** Declared type of a ref (data paths are dynamic → `any`). */
export function refType(entries: readonly RuleCatalogueEntry[], ref: string): string | null {
  const hit = entries.find((e) => e.ref === ref) ?? ENGINE_CATALOGUE.find((e) => e.ref === ref)
  if (hit) return hit.type
  if (ref.startsWith('data.') || ref.startsWith('event.') || ref.startsWith('event[')) return 'any'
  return null
}
